import { useState, useRef, useEffect } from "react";

import api from "@helpers/api";

import CustomModal from "@components/CustomModal";

import { Button, Input, Text } from "@fragments";

import Parser from "@helpers/parser";
import {
    createStripeConnectLink,
    fetchWallets,
    WALLET_PROVIDERS,
} from "@helpers/wallets";

import './styles.scss'

import { Alert, Skeleton } from "@mui/material";
import { PixRounded } from "@mui/icons-material";


export default function RequestWithdrawalModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState()

    const [loadingAccount, setLoadingAccount] = useState(true)
    const [account, setAccount] = useState(null)

    const REF_config = useRef({});
    const REF_callback = useRef();

    modalRef.current = {
        open(preFill, callback) {
            REF_config.current = { ...preFill };
            REF_callback.current = callback;
            setLoading(false)
            setError(null)
            if (preFill?.wallet) {
                setAccount({ ...preFill.wallet })
                setLoadingAccount(false)
            } else {
                setLoadingAccount(true)
                handleLoadAccount()
            }
            setOpen(true);
        },
    };

    useEffect(() => {
        if (loadingAccount) {
            handleLoadAccount()
        }
    }, [loadingAccount])


    async function handleSubmit() {
        try {
            if (isStripeAccount()) {
                await handleStripeDashboard()
                return
            }

            const requestAmount = parseInt(REF_config.current.amount) || 0;

            if (requestAmount < 500) {
                setError('Insira um valor maior que R$5,00 para realizar o saque')
                return
            }

            if (requestAmount > account?.balance?.available) {
                setError('Insira um valor de até R$ ' + Parser.price.brl(account?.balance?.available))
                return
            }

            setLoading(true);

            const { data } = await api.post("wallet/withdrawal", {
                provider: WALLET_PROVIDERS.ASAAS,
                amount: parseInt(REF_config.current.amount) || 0,
            });

            if (data?.result?.success) {
                await REF_callback.current(data);
                window.setSnackbar('O saque foi solicitado com sucesso')
                setOpen(false);
            } else {
                if (data.error === 'amount_not_allowed') {
                    setError('Insira um valor maior que R$5,00 para realizar o saque')
                } else if (data.result.fail_reason === 'nightly_limit_exceeded') {
                    setError('O valor do saque é superior ao limite Pix noturno por transação. Insira um valor de até R$ 1.000,00.')
                } else {
                    if (data.result.fail_reason === 'incomplete_receiver_information') {
                        setError('Complete seu cadastro para realizar o saque')
                    }
                }
            }
        } catch (e) {
            console.log(e);
        }

        setLoading(false);
    }

    async function handleLoadAccount() {
        const provider = REF_config.current.provider || WALLET_PROVIDERS.ASAAS
        const result = await fetchWallets();
        const nextAccount = provider === WALLET_PROVIDERS.STRIPE
            ? result.stripe
            : (result.asaas.raw || result.asaas)
        setAccount({ ...nextAccount })
        setLoadingAccount(false)
    }

    async function handleStripeDashboard() {
        setLoading(true)
        try {
            const link = await createStripeConnectLink({ type: 'dashboard' })
            if (REF_callback.current) await REF_callback.current(link)
            if (link?.url) {
                window.location.assign(link.url)
            }
        } catch (e) {
            console.log(e)
            setError('Nao foi possivel abrir o Stripe agora')
        }
        setLoading(false)
    }

    async function handleConfigurePixKey() {
        setLoading(true)
        try {
            setError(null)

            const { data } = await api.post("wallet/withdrawal/account", {
                provider: WALLET_PROVIDERS.ASAAS,
                key: REF_config.current.pixKey,
                keyType: REF_config.current.keyType || 'email'
            })

            if (data?.success) {
                if (REF_config.current.configureOnly) {
                    if (REF_callback.current) await REF_callback.current(data);
                    setOpen(false);
                } else {
                    setLoadingAccount(true)
                }
            } else {
                setError('Chave pix inválida')
            }
        } catch (e) {
            console.log(e)
            setError('Erro ao configurar chave pix')
        }

        setLoading(false)
    }

    function AccountBox() {
        if (!account?.withdrawal?.key) return null

        const keyType = account?.withdrawal?.keyType || 'email'

        const label = {
            email: 'Email',
            phone: 'Telefone',
            cpf: 'CPF',
            cnpj: 'CNPJ',
            evp: 'Chave aleatória'
        }[keyType]

        const keyFormatted = {
            email: Parser.email(account?.withdrawal?.key),
            phone: Parser.phone('+55' + account?.withdrawal?.key),
            cpf: Parser.cpf(account?.withdrawal?.key),
            cnpj: Parser.cnpj(account?.withdrawal?.key),
            evp: account?.withdrawal?.key
        }[keyType]


        return (
            <div className="current-account-box bsf-8 mt-3">
                <h5 className="bold">Chave pix para transferência</h5>

                <small className="pix-key-row">
                    <PixRounded fontSize="small" />
                    <h5 className="bold" style={{ transform: 'translateY(2px)' }}>
                        <b className="pr-1">{label}:</b>
                        <small>{keyFormatted}</small>
                    </h5>
                </small>
            </div>
        )
    }

    function StripeDashboardPanel() {
        return (
            <div className="current-account-box bsf-8 mt-3">
                <Text.h5 weight={500} contrast={8}>Stripe Express</Text.h5>
                <Text.span contrast={6}>
                    Saques em USD sao gerenciados pela conta Stripe conectada.
                </Text.span>
                <Text.caption contrast={6} className="mt-1">
                    Taxas: 9.49% + {Parser.price.format(199, 'USD')}
                </Text.caption>
            </div>
        )
    }

    function WithdrawalValueInput() {
        const [value, setValue] = useState(REF_config.current.amount || 0)

        useEffect(() => {
            REF_config.current.amount = value
        }, [value])

        function handleInputFullValue() {
            setValue(account?.balance?.available)
        }

        return (
            <div className="withdrawal-value-input">
                <Input.Money
                    className="mt-1 mb-1"
                    autoFocus={true}
                    value={value}
                    onInput={setValue}
                    label="Valor a ser sacado (mínimo R$5,00)"
                    type="money"
                />

                <Button
                    className="mt-2"
                    color="overlay"
                    text={`Solicitar valor total: R$ ${Parser.price.brl(account?.balance?.available || 0)}`}
                    onClick={handleInputFullValue}
                />
            </div>
        )
    }

    function ConfigurePixKey() {
        const [pixKey, setPixKey] = useState('')
        const [keyType, setKeyType] = useState(REF_config.current.keyType || 'email')

        useEffect(() => {
            REF_config.current.pixKey = pixKey
        }, [pixKey])

        const keyTypes = [
            {
                value: 'email',
                label: 'Email'
            },
            {
                value: 'phone',
                label: 'Telefone'
            },
            {
                value: 'cpf',
                label: 'CPF'
            },
            {
                value: 'cnpj',
                label: 'CNPJ'
            },
            {
                value: 'evp',
                label: 'Chave aleatória'
            }
        ]

        useEffect(() => {
            REF_config.current.keyType = keyType
        }, [keyType])

        function KeyTypeBox({ value, label }) {
            return (
                <Button
                    color={keyType === value ? 'primary' : 'overlay'}
                    text={label}
                    size="small"
                    onClick={() => {
                        setKeyType(value)
                        setPixKey('')
                    }}
                />
            )
        }

        function renderPixKeyInput() {
            switch (keyType) {
                case 'phone':
                    return (
                        <Input.Phone
                            className="mt-1"
                            label="Insira o número de telefone"
                            value={pixKey}
                            onInput={setPixKey}
                        />
                    )
                case 'cpf':
                    return (
                        <Input.CPF
                            className="mt-1"
                            label="Insira o CPF"
                            value={pixKey}
                            onInput={setPixKey}
                        />
                    )
                case 'cnpj':
                    return (
                        <Input.CNPJ
                            className="mt-1"
                            label="Insira o CNPJ"
                            value={pixKey}
                            onInput={setPixKey}
                        />
                    )

                case 'email':
                    return (
                        <Input.Email
                            className="mt-1"
                            label="Insira o email"
                            value={pixKey}
                            onInput={setPixKey}
                        />
                    )
                case 'evp':
                default:
                    return (
                        <Input.Text
                            className="mt-1"
                            label="Insira a chave pix aleatória"
                            value={pixKey}
                            onInput={setPixKey}
                        />
                    )
            }
        }

        return (
            <div>
                <h4 className="bold mt-3">Configure sua chave pix para transferência</h4>
                <h5>Para realizar o saque, você primeiro deve informar sua chave pix.</h5>


                <h5 className="pt-2 bold">Tipo de chave</h5>
                <div className="mt-1 key-type-boxes">
                    {
                        keyTypes.map((keyType) => (
                            <KeyTypeBox key={keyType.value} value={keyType.value} label={keyType.label} />
                        ))
                    }
                </div>

                <h5 className="pt-3 bold">Chave pix</h5>
                {renderPixKeyInput()}
            </div>
        )
    }

    function isStripeAccount() {
        return (REF_config.current.provider || account?.provider) === WALLET_PROVIDERS.STRIPE
    }

    const hasPixKeyConfigured = !!account?.withdrawal?.key && !!account?.withdrawal?.keyType
    const stripeAccount = isStripeAccount()

    return (
        <CustomModal
            maxWidth={435}
            bottomButtonSumbitText={
                stripeAccount ? 'Abrir Stripe' : (hasPixKeyConfigured ? 'Solicitar saque' : 'Configurar chave pix')
            }
            bottomButtonCancelText="Cancelar"
            loading={loading || loadingAccount}
            title={stripeAccount ? 'Saque internacional' : 'Sacar valor'}
            className="withdrawal-modal"
            handleSubmit={stripeAccount ? handleStripeDashboard : (hasPixKeyConfigured ? handleSubmit : handleConfigurePixKey)}
            open={open}
            handleClose={() => setOpen(false)}
            bodyComponent={
                open ? (
                    <div className="px-4 py-1">
                        {
                            loadingAccount ? <Skeleton variant="rectangular" height={100} /> : (
                                <>

                                    {
                                        stripeAccount ? <StripeDashboardPanel /> : hasPixKeyConfigured ? <>
                                            <AccountBox />
                                            <h5 className="bold mt-3">Insira o valor do saque</h5>
                                            <WithdrawalValueInput />
                                        </> : <ConfigurePixKey />
                                    }
                                </>
                            )
                        }


                        {
                            error && <Alert className="mt-1 error-message" severity="error">{error}</Alert>
                        }
                    </div>
                ) : null
            }
        />
    );
}
